import {
  IconButton,
  List,
  ListBody,
  ListCell,
  ListHead,
  ListHeadCell,
  ListRow,
  Loader,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@plaidev/sour-react';
import type { MouseEvent } from 'react';
import type {} from 'react/jsx-runtime';
import { DomainV2 } from 'troppus-common/domain';

import SourceSystem = DomainV2.Import.SourceSystem;

export type DeleteTarget = {
  id: SourceSystem.Id;
  displayName: string;
};

type SourceSystemListProps = {
  rows: SourceSystem[];
  loading: boolean;
  isProjectAdmin: boolean;
  onEdit: (id: SourceSystem.Id) => void;
  onAddTableDefinition: (id: SourceSystem.Id) => void;
  onDelete: (target: DeleteTarget) => void;
};

/**
 * @feature ソースシステム一覧テーブル
 * @capabilities 表示名・説明・状態・更新日時の一覧表示, 編集シート表示, テーブル定義追加, 削除
 * @behavior 行クリック→編集シート表示, ⋯メニュー「テーブル定義を追加」→テーブル定義作成画面へ遷移, 「削除」→削除確認ダイアログ。操作メニューはプロジェクト管理者のみ表示
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
export function SourceSystemList({ rows, loading, isProjectAdmin, onEdit, onAddTableDefinition, onDelete }: SourceSystemListProps) {
  return (
    <List
      loader={
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      }
      loading={loading}
    >
      <ListHead>
        <ListRow>
          <ListHeadCell>表示名</ListHeadCell>
          <ListHeadCell>説明</ListHeadCell>
          <ListHeadCell width={100}>状態</ListHeadCell>
          <ListHeadCell width={160}>更新日時</ListHeadCell>
          <ListHeadCell width={56} />
        </ListRow>
      </ListHead>
      <ListBody>
        {rows.map((row) => (
          <ListRow key={row.id} className="cursor-pointer hover:bg-action-subtle-hover" onClick={() => onEdit(row.id)}>
            <ListCell>
              <span className="block max-w-0 min-w-full truncate text-sm font-medium text-default">{row.displayName}</span>
            </ListCell>
            <ListCell>
              <span className="block max-w-0 min-w-full truncate text-sm text-muted-light">{row.description ?? '—'}</span>
            </ListCell>
            <ListCell width={100}>
              <span className="text-sm">{row.isActive ? '有効' : '無効'}</span>
            </ListCell>
            <ListCell width={160}>
              <span className="text-sm text-muted-light">{row.updatedAt.format('YYYY-MM-DD HH:mm')}</span>
            </ListCell>
            <ListCell width={56}>
              {isProjectAdmin ? (
                // biome-ignore lint/a11y/noStaticElementInteractions: stop row click; menu items handle their own a11y
                <span
                  className="flex justify-end"
                  onClick={(e: MouseEvent<HTMLSpanElement>) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Menu>
                    <MenuTrigger>
                      <IconButton size="sm" as="div" variant="quiet" aria-label="操作メニュー">
                        <span aria-hidden className="text-base leading-none">
                          ⋯
                        </span>
                      </IconButton>
                    </MenuTrigger>
                    <MenuContent align="end" className="min-w-[200px]">
                      <MenuItem onSelect={() => onAddTableDefinition(row.id)}>テーブル定義を追加</MenuItem>
                      <MenuItem color="negative" onSelect={() => onDelete({ id: row.id, displayName: row.displayName })}>
                        削除
                      </MenuItem>
                    </MenuContent>
                  </Menu>
                </span>
              ) : null}
            </ListCell>
          </ListRow>
        ))}
      </ListBody>
    </List>
  );
}
